export const ORDERS_SUBTAB_CAPS = {
  credits: 'admin_panel_orders_credits',
  batches: 'admin_panel_credit_batches',
  products: 'admin_panel_orders_products',
  balances: 'admin_panel_balances_organizations',
  ledger: 'admin_panel_ledger_organizations',
} as const;

export interface OrdersMeta {
  permissions: {
    credits: boolean;
    batches: boolean;
    products: boolean;
    balances: boolean;
    ledger: boolean;
  };
  orgs: Array<{ id: number; name: string }>;
  credit_order_statuses: Array<{ value: string; label: string }>;
  product_order_statuses: Array<{ value: string; label: string }>;
  batch_statuses: Array<{ value: string; label: string }>;
  ledger_types: Array<{ value: string; label: string }>;
  ledger_statuses: Array<{ value: string; label: string }>;
  currencies: Array<{ id: number; code: string; name: string }>;
  credit_expiry_months: number;
}

export interface CreditOrderRow {
  id: number;
  org: number | null;
  org_name: string | null;
  user: number;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  credits: number;
  currency_code: string;
  cost_per_credit: string;
  total_cost: string;
  status: string;
  created_at: string | null;
  approved_by: number | null;
  approved_by_email: string | null;
  approved_at: string | null;
  batch_id: number | null;
  batch_expires_at: string | null;
}

export interface CreditOrderFormValue {
  org: number | '';
  credits: string;
  currency_code: string;
  cost_per_credit: string;
  total_cost: string;
}

export interface CreditBatchRow {
  id: number;
  org: number;
  org_name: string | null;
  amount_granted: string;
  amount_remaining: string;
  currency: number | null;
  currency_code: string | null;
  granted_at: string | null;
  expires_at: string | null;
  note: string;
  status: string;
  approved_by: number | null;
  approved_by_email: string | null;
  credit_order: number | null;
  created_at: string | null;
}

export interface ProjectSummary {
  id: number;
  name: string;
  lifecycle: string;
  status: string;
  opportunity_space: string;
  markets: string[];
  territory_demand_space: string;
  gather_project_info: boolean;
  org: number | null;
  org_name: string | null;
}

export interface ProductOrderRow {
  id: number;
  order_number: string;
  user: number;
  user_email: string | null;
  user_first_name: string;
  user_last_name: string;
  user_display_name: string;
  product: number;
  product_name: string | null;
  product_service_type: string | null;
  credits: number;
  quantity: number;
  unit_price: string;
  total_price: string;
  currency: string;
  order_status: string;
  markets: string;
  additional_info: string;
  created_at: string | null;
  completed_at: string | null;
  project: ProjectSummary | null;
  org_id: number | null;
  org_name: string | null;
  ledger?: {
    id: number;
    amount: string;
    status: string;
    type: string;
  } | null;
}

export interface ProductOrderFormValue {
  order_number: string;
  credits: string;
  markets: string[];
  order_status: string;
  currency: string;
  additional_info: string;
  opportunity_space: string;
  project_markets: string[];
  gather_project_info: boolean;
  territory_demand_space: string;
}

export interface OrgBalanceRow {
  org: number;
  org_name: string;
  credits_marketplace: string | null;
  tokens_blueprint: string | null;
  updated_at: string | null;
  authoritative_total: string | null;
  is_unlimited: boolean;
}

export interface OrgLedgerRow {
  id: number;
  org: number;
  org_name: string | null;
  amount: string;
  type: string;
  status: string;
  reason: string;
  order: number | null;
  created_at: string | null;
  user: number | null;
  user_email: string | null;
  allocations: Array<{
    batch_id: number;
    amount: string;
    batch_expires_at: string | null;
  }>;
}

export function formFromCreditOrder(row: CreditOrderRow): CreditOrderFormValue {
  return {
    org: row.org ?? '',
    credits: String(row.credits ?? 0),
    currency_code: row.currency_code || 'CHF',
    cost_per_credit: row.cost_per_credit || '0',
    total_cost: row.total_cost || '0',
  };
}

export function creditOrderToPayload(value: CreditOrderFormValue) {
  const credits = Number(value.credits) || 0;
  const cost = Number(value.cost_per_credit) || 0;
  return {
    org: value.org === '' ? null : Number(value.org),
    credits,
    currency_code: value.currency_code,
    cost_per_credit: value.cost_per_credit,
    total_cost: (credits * cost).toFixed(2),
  };
}

export function parseMarketsString(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function formFromProductOrder(row: ProductOrderRow): ProductOrderFormValue {
  const project = row.project;
  return {
    order_number: row.order_number || '',
    credits: String(row.credits ?? 0),
    markets: parseMarketsString(row.markets),
    order_status: row.order_status || 'Draft',
    currency: row.currency || 'CHF',
    additional_info: row.additional_info || '',
    opportunity_space: project?.opportunity_space || '',
    project_markets: project?.markets ? [...project.markets] : [],
    gather_project_info: Boolean(project?.gather_project_info),
    territory_demand_space: project?.territory_demand_space || '',
  };
}

export function productOrderToPayload(value: ProductOrderFormValue) {
  return {
    order_number: value.order_number.trim(),
    credits: Number(value.credits) || 0,
    markets: value.markets.join(', '),
    currency: value.currency,
    additional_info: value.additional_info.trim() || null,
    project_fields: {
      opportunity_space: value.opportunity_space,
      markets: value.project_markets,
      gather_project_info: value.gather_project_info,
      territory_demand_space: value.territory_demand_space,
    },
  };
}
